import { useCallback, useEffect, useRef, useState } from 'react';
import { useResource } from 'hooks/useResource';
import healthScoresService from 'services/healthScoresService';

// Summaries are only regenerated by the nightly run when a classification changes, so a
// viewed account can sit on a missing or long-stale summary. Anything older than this
// gets regenerated on demand (CR-6601).
const SUMMARY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// LP360 renders two consumers of this hook (the KPI row and the health score panel), each
// with its own state. Sharing the request keeps one page view to one set of LLM calls
// instead of one per consumer. The stored promise never rejects (a failure resolves to
// null), so a consumer arriving late cannot inherit an earlier rejection.
const inFlightRegenerations = new Map();

const regenerateOnce = (accountId) => {
	if (!inFlightRegenerations.has(accountId)) {
		inFlightRegenerations.set(
			accountId,
			healthScoresService
				.regenerateSummary({ salesforceObjectId: accountId })
				// The stale summary is still worth showing, so a failure just resolves empty
				.catch(() => null)
				.finally(() => inFlightRegenerations.delete(accountId))
		);
	}

	return inFlightRegenerations.get(accountId);
};

export const isSummaryStale = (healthScore, now = Date.now()) => {
	if (!healthScore) return false;
	if (!healthScore.summary || !healthScore.summary_generated_at) return true;

	const generatedAt = new Date(healthScore.summary_generated_at).getTime();
	if (Number.isNaN(generatedAt)) return true;

	return now - generatedAt > SUMMARY_MAX_AGE_MS;
};

// Latest stored health score for an Account, or null when none has been
// calculated yet. Shared by the LP360 KPI (CR-6567) and the Relationship
// Health Score component (CR-6569).
//
// When the stored summary is stale, the stale one is returned right away and a
// regenerated score replaces it once the request comes back, so the panel renders
// immediately instead of waiting on the AI call.
const useAccountHealthScores = (accountId) => {
	const fetchFn = useCallback(async () => {
		if (!accountId) return null;
		const scores = await healthScoresService.getHealthScores({
			salesforceObjectId: accountId,
		});
		// Endpoint returns an array ordered most-recent first; take the latest
		return scores?.[0] ?? null;
	}, [accountId]);

	const { data, loading, error } = useResource({ fetchFn });

	// Kept with the account it belongs to so a switch of accounts cannot show the
	// previous account's regenerated summary.
	const [regenerated, setRegenerated] = useState(null);
	const [regeneratingSummary, setRegeneratingSummary] = useState(false);
	// Keyed to the account that failed so switching accounts never shows a
	// foreign error
	const [summaryErrorFor, setSummaryErrorFor] = useState(null);
	// One attempt per account: `data` changes identity on every fetch, and a failed
	// regeneration leaves the summary stale, so without this the effect would loop.
	const requestedFor = useRef(null);

	// A `data` identity change tears this effect down mid-request, so the cleanup
	// cannot discard the response or clear the indicator itself -- both are chained
	// onto the promise the cleanup has stored, which outlives the teardown.
	const pendingRegeneration = useRef(null);

	useEffect(() => {
		if (!accountId || requestedFor.current === accountId) return;
		if (!isSummaryStale(data)) return;

		requestedFor.current = accountId;
		setRegeneratingSummary(true);

		const request = regenerateOnce(accountId);
		pendingRegeneration.current = request;

		// The stored promise never rejects; a failure arrives as null
		request
			.then((score) => {
				// `regenerated` is keyed to the account it belongs to, so a response for a
				// since-changed account is dropped on render rather than stored
				if (score) {
					setRegenerated({ accountId, score });
				} else {
					setSummaryErrorFor(accountId);
				}
			})
			.finally(() => setRegeneratingSummary(false));

		// If this run is torn down while the request is still open, the request itself
		// settles the indicator once it finishes
		return () => {
			request.finally(() => {
				if (pendingRegeneration.current === request) {
					setRegeneratingSummary(false);
				}
			});
		};
	}, [accountId, data]);

	const healthScores =
		regenerated?.accountId === accountId ? regenerated.score : data;

	return {
		healthScores,
		loading,
		error,
		regeneratingSummary,
		summaryError: summaryErrorFor === accountId,
	};
};

export default useAccountHealthScores;
